import React, { useMemo } from "react";
import { Drawer, Select, DatePicker, Button, Space, Badge } from "antd";
import { FilterOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const OrderFilterDrawer = ({
  open,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  locationOptions = [],
  locationLoading = false,
}) => {
  // Convert date strings to dayjs objects for the RangePicker
  const dateRange = useMemo(() => {
    if (filters.created_from && filters.created_to) {
      return [dayjs(filters.created_from), dayjs(filters.created_to)];
    }
    return null;
  }, [filters.created_from, filters.created_to]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      onFilterChange("created_from", dates[0].format("YYYY-MM-DD"));
      onFilterChange("created_to", dates[1].format("YYYY-MM-DD"));
    } else {
      onFilterChange("created_from", undefined);
      onFilterChange("created_to", undefined);
    }
  };

  const handleLocationChange = (value) => {
    onFilterChange("location_id", value);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.location_id) count++;
    if (filters.created_from && filters.created_to) count++;
    return count;
  }, [filters]);

  return (
    <Drawer
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FilterOutlined />
            <span>Filter Orders</span>
            {activeFilterCount > 0 && (
              <Badge
                count={activeFilterCount}
                style={{
                  backgroundColor: "#F9A022",
                }}
              />
            )}
          </div>
        </div>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      closeIcon={<CloseOutlined />}
      footer={
        <div className="flex justify-between items-center">
          <Button
            type="link"
            onClick={onClearFilters}
            disabled={!hasActiveFilters}
            className="text-gray-600 hover:text-orange-500 px-0"
          >
            Clear All Filters
          </Button>
          <Button
            type="primary"
            onClick={onClose}
            className="bg-gradient-to-r from-[#D06D01] to-[#F9A022] hover:bg-gradient-to-l hover:from-[#F9A022] hover:to-[#D06D01] border-0"
          >
            Close
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Location Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <Select
            placeholder="Select location"
            style={{ width: "100%" }}
            value={filters.location_id}
            onChange={handleLocationChange}
            loading={locationLoading}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            options={locationOptions}
          />
        </div>

        {/* Date Range Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order Created Date Range
          </label>
          <RangePicker
            style={{ width: "100%" }}
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["Start Date", "End Date"]}
          />
          {filters.created_from && filters.created_to && (
            <p className="text-xs text-gray-500 mt-1">
              {dayjs(filters.created_from).format("MMM D, YYYY")} -{" "}
              {dayjs(filters.created_to).format("MMM D, YYYY")}
            </p>
          )}
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Active Filters:
            </p>
            <div className="space-y-1">
              {filters.location_id && (
                <p className="text-xs text-gray-600">
                  • Location:{" "}
                  {locationOptions.find(
                    (loc) => loc.value === filters.location_id
                  )?.label || filters.location_id}
                </p>
              )}
              {filters.created_from && filters.created_to && (
                <p className="text-xs text-gray-600">
                  • Date: {dayjs(filters.created_from).format("MMM D, YYYY")} -{" "}
                  {dayjs(filters.created_to).format("MMM D, YYYY")}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default OrderFilterDrawer;
